import { useState, useCallback, useEffect, useRef, RefObject } from 'react';

/**
 * 拖拽文件类型
 */
export type DragDropType = 'img' | 'attachment';

/**
 * 拖拽配置选项
 */
export interface UseEditorDragDropOptions {
  /** 是否启用图片拖拽上传 */
  enableDraggingImageUpload?: boolean;
  /** 是否启用附件拖拽上传 */
  enableDraggingAttachmentUpload?: boolean;
  /** 容器引用，用于检测拖拽是否离开容器 */
  containerRef: RefObject<HTMLElement>;
}

/**
 * 拖拽回调函数
 */
export interface DragDropCallbacks {
  /** 图片上传处理函数 */
  onImageDrop?: (files: File[]) => void;
  /** 附件上传处理函数 */
  onAttachmentDrop?: (files: File[]) => void;
}

/**
 * 拖拽状态
 */
export interface DragDropState {
  /** 是否正在拖拽 */
  dragging: boolean;
  /** 是否全部是图片 */
  draggingAllImg: boolean;
}

/**
 * 拖拽事件处理器
 */
export interface DragDropHandlers {
  /** dragenter 事件处理 */
  handleDragEnter: (e: DragEvent) => void;
  /** dragover 事件处理 */
  handleDragOver: (e: DragEvent) => void;
  /** dragleave 事件处理 */
  handleDragLeave: (e: DragEvent) => void;
  /** dragend 事件处理 */
  handleDragEnd: (e: DragEvent) => void;
  /** drop 事件处理（用于 React 组件） */
  handleDrop: (e: DragEvent | React.DragEvent, type: DragDropType) => void;
}

/**
 * 拖拽 Hook 返回值
 */
export interface UseEditorDragDropReturn {
  /** 拖拽状态 */
  state: DragDropState;
  /** 拖拽事件处理器 */
  handlers: DragDropHandlers;
}

/**
 * 检查 DataTransfer 是否包含文件
 */
const hasFiles = (dataTransfer: DataTransfer | null): boolean => {
  if (!dataTransfer) return false;
  return [...dataTransfer.items].some(item => item.kind === 'file');
};

/**
 * 检查 DataTransfer 是否全部是图片
 */
const isAllImages = (dataTransfer: DataTransfer | null): boolean => {
  if (!dataTransfer) return false;
  const items = [...dataTransfer.items];
  if (items.length === 0) return false;
  return items.every(item => item.type.startsWith('image/'));
};

/**
 * 从 DataTransfer 中提取文件列表
 */
const extractFiles = (dataTransfer: DataTransfer): File[] => {
  const files: File[] = [];

  // Chrome有items属性，对Chrome的单独处理
  if (dataTransfer.items !== undefined) {
    for (let i = 0; i < dataTransfer.items.length; i++) {
      const item = dataTransfer.items[i];
      // 用webkitGetAsEntry禁止上传文件夹
      if (item.kind === 'file' && item.webkitGetAsEntry()?.isFile) {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
  } else {
    // 兼容其他浏览器
    files.push(...Array.from(dataTransfer.files));
  }

  return files;
};

/**
 * 编辑器拖拽上传 Hook
 *
 * 提供统一的拖拽文件上传功能，支持图片和附件两种类型
 *
 * @example
 * ```tsx
 * const containerRef = useRef<HTMLDivElement>(null);
 * const { state, handlers } = useEditorDragDrop({
 *   enableDraggingImageUpload: true,
 *   enableDraggingAttachmentUpload: true,
 *   containerRef,
 *   onImageDrop: (files) => {
 *     // 处理图片上传
 *   },
 *   onAttachmentDrop: (files) => {
 *     // 处理附件上传
 *   },
 * });
 *
 * // 在容器上绑定事件
 * useEffect(() => {
 *   const node = containerRef.current;
 *   if (!node) return;
 *   node.addEventListener('dragover', handlers.handleDragOver);
 *   node.addEventListener('dragenter', handlers.handleDragEnter);
 *   node.addEventListener('dragleave', handlers.handleDragLeave);
 *   node.addEventListener('dragend', handlers.handleDragEnd);
 *   return () => {
 *     node.removeEventListener('dragover', handlers.handleDragOver);
 *     node.removeEventListener('dragenter', handlers.handleDragEnter);
 *     node.removeEventListener('dragleave', handlers.handleDragLeave);
 *     node.removeEventListener('dragend', handlers.handleDragEnd);
 *   };
 * }, [handlers]);
 * ```
 */
export const useEditorDragDrop = (
  options: UseEditorDragDropOptions,
  callbacks: DragDropCallbacks = {}
): UseEditorDragDropReturn => {
  const { enableDraggingImageUpload = true, enableDraggingAttachmentUpload = true, containerRef } = options;

  const { onImageDrop, onAttachmentDrop } = callbacks;

  // 拖拽状态管理
  const [dragging, setDragging] = useState(false);
  const [draggingAllImg, setDraggingAllImg] = useState(false);
  // debounce 定时器引用
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // dragover 事件处理
  const handleDragOver = useCallback((e: DragEvent) => {
    // 在捕获阶段，我们需要允许事件继续传播到编辑器
    // 但也要确保允许 drop（preventDefault）
    // 注意：不调用 stopPropagation，让事件可以继续传播到编辑器内部
    if (hasFiles(e.dataTransfer)) {
      e.preventDefault();
    }

    // 清除之前的定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // 设置新的 debounce 定时器，500ms 后隐藏蒙层
    debounceTimerRef.current = setTimeout(() => {
      setDragging(false);
      debounceTimerRef.current = null;
    }, 500);
  }, []);

  // dragenter 事件处理
  const handleDragEnter = useCallback((e: DragEvent) => {
    if (!hasFiles(e.dataTransfer)) return;

    // 清除 debounce 定时器，防止在拖拽过程中隐藏蒙层
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    setDraggingAllImg(false);
    if (isAllImages(e.dataTransfer)) {
      setDraggingAllImg(true);
    }
    setDragging(true);

    // 在捕获阶段，允许事件继续传播，但确保允许 drop
    e.preventDefault();
    // 注意：不调用 stopPropagation，让事件可以继续传播到编辑器内部
    debounceTimerRef.current = setTimeout(() => {
      setDragging(false);
    }, 500);
  }, []);

  // dragleave 事件处理
  const handleDragLeave = useCallback(
    (e: DragEvent) => {
      if (!hasFiles(e.dataTransfer)) return;

      // 检查是否还在容器内
      // 在捕获阶段，我们需要检查 relatedTarget 是否还在容器内
      // 如果 relatedTarget 不在容器内，说明拖拽已经离开了容器
      const relatedTarget = e.relatedTarget as Node | null;
      if (relatedTarget && containerRef.current?.contains(relatedTarget)) {
        return;
      }

      // 如果 relatedTarget 为 null 或者不在容器内，检查当前目标
      // 如果当前目标也不在容器内，说明确实离开了
      const target = e.target as Node;
      if (target && containerRef.current?.contains(target)) {
        return;
      }

      // 如果确实离开了容器，清除定时器并隐藏蒙层
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      debounceTimerRef.current = setTimeout(() => {
        setDragging(false);
        debounceTimerRef.current = null;
      }, 500);

      // 注意：不立即隐藏，让 debounce 机制处理，避免频繁闪烁
      // 如果确实离开了，dragover 不会再触发，定时器会在 500ms 后隐藏
    },
    [containerRef]
  );

  // dragend 事件处理
  const handleDragEnd = useCallback((e: DragEvent) => {
    if (!hasFiles(e.dataTransfer)) return;

    // 清除 debounce 定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    debounceTimerRef.current = setTimeout(() => {
      setDragging(false);
      debounceTimerRef.current = null;
    }, 500);
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // drop 事件处理
  const handleDrop = useCallback(
    (e: DragEvent | React.DragEvent, type: DragDropType) => {
      const dataTransfer = e instanceof DragEvent ? e.dataTransfer : e.dataTransfer;
      if (!hasFiles(dataTransfer) || !dataTransfer) return;

      // 清除 debounce 定时器
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      e.preventDefault();
      e.stopPropagation();
      setDragging(false);

      const files = extractFiles(dataTransfer);
      if (!files.length) return;

      if (type === 'img' && files.every(file => file.type.startsWith('image/'))) {
        // 处理图片上传
        if (enableDraggingImageUpload && onImageDrop) {
          onImageDrop(files);
        }
        return;
      }

      // 处理附件上传
      if (type === 'attachment' && enableDraggingAttachmentUpload && onAttachmentDrop) {
        onAttachmentDrop(files);
      }
    },
    [enableDraggingImageUpload, enableDraggingAttachmentUpload, onImageDrop, onAttachmentDrop]
  );

  return {
    state: {
      dragging,
      draggingAllImg,
    },
    handlers: {
      handleDragEnter,
      handleDragOver,
      handleDragLeave,
      handleDragEnd,
      handleDrop,
    },
  };
};

/**
 * 自动绑定拖拽事件监听器的 Hook
 *
 * 这是 useEditorDragDrop 的便捷封装，自动处理事件监听器的绑定和清理
 *
 * **注意**：此 Hook 使用捕获阶段（capture phase）来监听事件，这样可以确保在事件到达编辑器
 * 内部（如 TinyMCE 的 iframe）之前就捕获到事件，避免编辑器内部阻止事件冒泡导致蒙层无法显示的问题。
 *
 * @example
 * ```tsx
 * const containerRef = useRef<HTMLDivElement>(null);
 * const { state, handlers } = useEditorDragDropWithListeners({
 *   enableDraggingImageUpload: true,
 *   enableDraggingAttachmentUpload: true,
 *   containerRef,
 *   onImageDrop: (files) => {
 *     // 处理图片上传
 *   },
 *   onAttachmentDrop: (files) => {
 *     // 处理附件上传
 *   },
 * });
 *
 * // 事件监听器已自动绑定，无需手动处理
 * ```
 */
export const useEditorDragDropWithListeners = (
  options: UseEditorDragDropOptions,
  callbacks: DragDropCallbacks = {}
): UseEditorDragDropReturn => {
  const { state, handlers } = useEditorDragDrop(options, callbacks);

  // 自动绑定事件监听器
  // 使用捕获阶段（capture phase）来监听事件，这样可以在事件到达编辑器内部之前就捕获到
  // 避免编辑器内部阻止事件冒泡导致蒙层无法显示的问题
  useEffect(() => {
    const container = options.containerRef.current;
    if (!container) return;

    // 使用捕获阶段监听事件
    const captureOptions = { capture: true };

    container.addEventListener('dragover', handlers.handleDragOver, captureOptions);
    container.addEventListener('dragenter', handlers.handleDragEnter, captureOptions);
    container.addEventListener('dragleave', handlers.handleDragLeave, captureOptions);
    container.addEventListener('dragend', handlers.handleDragEnd, captureOptions);

    return () => {
      container.removeEventListener('dragover', handlers.handleDragOver, captureOptions);
      container.removeEventListener('dragenter', handlers.handleDragEnter, captureOptions);
      container.removeEventListener('dragleave', handlers.handleDragLeave, captureOptions);
      container.removeEventListener('dragend', handlers.handleDragEnd, captureOptions);
    };
  }, [options.containerRef, handlers]);

  return { state, handlers };
};
